import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Bell, Mail, MapPin, Truck, DollarSign, 
  CheckCircle, ArrowRight, Filter, Zap
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_truck-connect-5/artifacts/s9sebpxc_tempus%20dispatch%20logo.jpg";

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const EQUIPMENT_TYPES = [
  { value: 'sprinter_van', label: 'Sprinter Van' },
  { value: 'cargo_van', label: 'Cargo Van' },
  { value: 'box_truck', label: 'Box Truck' },
  { value: 'hotshot', label: 'Hotshot' },
  { value: 'dry_van', label: 'Dry Van' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'step_deck', label: 'Step Deck' },
  { value: 'conestoga', label: 'Conestoga' },
  { value: 'lowboy', label: 'Lowboy' },
  { value: 'rgn', label: 'RGN (Removable Gooseneck)' },
  { value: 'double_drop', label: 'Double Drop' },
  { value: 'curtain_side', label: 'Curtain Side' },
  { value: 'tanker', label: 'Tanker' },
  { value: 'hopper', label: 'Hopper' },
  { value: 'dump_truck', label: 'Dump Truck' },
  { value: 'power_only', label: 'Power Only' }
];

const LoadAlertsPage = () => {
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    preferred_states: [],
    equipment_types: [],
    min_rate: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleState = (state) => {
    setFormData(prev => ({
      ...prev,
      preferred_states: prev.preferred_states.includes(state)
        ? prev.preferred_states.filter(s => s !== state)
        : [...prev.preferred_states, state]
    }));
  };

  const toggleEquipment = (type) => {
    setFormData(prev => ({
      ...prev,
      equipment_types: prev.equipment_types.includes(type)
        ? prev.equipment_types.filter(t => t !== type)
        : [...prev.equipment_types, type]
    }));
  };

  const selectAllStates = () => {
    setFormData(prev => ({ ...prev, preferred_states: [...US_STATES] }));
  };

  const clearAllStates = () => {
    setFormData(prev => ({ ...prev, preferred_states: [] }));
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.name) {
      toast.error('Please enter your name and email');
      return;
    }
    
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        min_rate: formData.min_rate ? parseFloat(formData.min_rate) : null
      };
      
      const res = await axios.post(`${API}/alerts/subscribe`, payload);
      setSubscriptionId(res.data.subscription_id);
      setSuccess(true);
      toast.success('Successfully subscribed to load alerts!');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-slate-800/50 border-slate-700 text-center" data-testid="success-card">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">You&apos;re All Set!</h2>
            <p className="text-gray-400 mb-6">
              Load alerts will be sent to <strong className="text-white">{formData.email}</strong> when matching loads become available.
            </p>
            
            <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-semibold text-gray-300 mb-2">Your Preferences:</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                {formData.preferred_states.length > 0 && (
                  <li className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    States: {formData.preferred_states.slice(0, 10).join(', ')}{formData.preferred_states.length > 10 ? ` +${formData.preferred_states.length - 10} more` : ''}
                  </li>
                )}
                {formData.equipment_types.length > 0 && (
                  <li className="flex items-start gap-2">
                    <Truck className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    Equipment: {formData.equipment_types.join(', ')}
                  </li>
                )}
                {formData.min_rate && (
                  <li className="flex items-start gap-2">
                    <DollarSign className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Minimum Rate: ${formData.min_rate}
                  </li>
                )}
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={() => window.location.href = '/loads'}
                data-testid="browse-loads-btn"
              >
                Browse Loads Now
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => window.location.href = '/'}
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4" data-testid="load-alerts-page">
      <Toaster position="top-right" />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <a href="/">
            <img src={LOGO_URL} alt="Tempus Dispatch" className="h-20 mx-auto mb-4" />
          </a>
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Load Alerts</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Get notified instantly when loads matching your preferences are posted. Never miss a good load again!
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  Alert Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Contact Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Your Name *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      data-testid="alert-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Email Address *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                      data-testid="alert-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-300">Phone (Optional)</Label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                {/* State Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-gray-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-500" />
                      Preferred Pickup States
                    </Label>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={selectAllStates} className="text-xs text-blue-400">
                        Select All
                      </Button>
                      <Button size="sm" variant="ghost" onClick={clearAllStates} className="text-xs text-gray-400">
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-10 gap-1 p-3 bg-slate-700/30 rounded-lg" data-testid="states-grid">
                    {US_STATES.map(state => (
                      <button
                        key={state}
                        onClick={() => toggleState(state)}
                        className={`p-1.5 text-xs font-medium rounded transition-colors ${
                          formData.preferred_states.includes(state)
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-600/50 text-gray-400 hover:bg-slate-600'
                        }`}
                      >
                        {state}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.preferred_states.length} states selected
                  </p>
                </div>

                {/* Equipment Types */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2 mb-2">
                    <Truck className="w-4 h-4 text-orange-500" />
                    Equipment Types
                  </Label>
                  <div className="flex flex-wrap gap-2" data-testid="equipment-grid">
                    {EQUIPMENT_TYPES.map(type => (
                      <button
                        key={type.value}
                        onClick={() => toggleEquipment(type.value)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          formData.equipment_types.includes(type.value)
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-600/50 text-gray-400 hover:bg-slate-600'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Min Rate */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    Minimum Rate (Optional)
                  </Label>
                  <Input
                    name="min_rate"
                    type="number"
                    value={formData.min_rate}
                    onChange={handleChange}
                    placeholder="e.g., 2500"
                    className="mt-1 bg-slate-700/50 border-slate-600 text-white w-48"
                    data-testid="min-rate-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Only get alerts for loads above this total rate
                  </p>
                </div>

                {/* Submit */}
                <Button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 py-6 text-lg"
                  data-testid="subscribe-btn"
                >
                  {loading ? 'Subscribing...' : (
                    <>
                      Subscribe to Alerts <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
              <CardContent className="p-6 text-center">
                <Zap className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">Instant Notifications</h3>
                <p className="text-sm text-gray-400">
                  Get email alerts within minutes of new loads being posted
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5 text-orange-500" />
                  Why Set Alerts?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-400">Never miss high-paying loads in your area</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-400">Be first to bid on new opportunities</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-400">Customize alerts to your equipment & routes</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-400">Unsubscribe anytime with one click</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-400">
                  Already have alerts?{' '}
                  <a href="/loads" className="text-blue-400 hover:underline">
                    Browse loads now
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadAlertsPage;
